/**
 * Jobs React Query hooks
 * Handles fetching, creating, updating, and deleting jobs
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNotification } from '@/stores/useNotificationStore';
import * as jobsApi from '@/services/jobsApi';
import { STALE_TIME } from '@/utils/constants';
import type { JobFilter, CreateJobInput, UpdateJobInput, Job, JobsResponse } from '@/types/job';

/**
 * Query keys for jobs
 */
export const jobKeys = {
  all: ['jobs'] as const,
  lists: () => [...jobKeys.all, 'list'] as const,
  list: (filter: JobFilter) => [...jobKeys.lists(), filter] as const,
  details: () => [...jobKeys.all, 'detail'] as const,
  detail: (id: string) => [...jobKeys.details(), id] as const,
  progress: (id: string) => [...jobKeys.detail(id), 'progress'] as const,
  cells: (id: string) => [...jobKeys.detail(id), 'cells'] as const,
};

/**
 * Hook to fetch paginated list of jobs
 */
export function useJobs(filter?: JobFilter) {
  return useQuery({
    queryKey: jobKeys.list(filter || {}),
    queryFn: () => jobsApi.getJobs(filter),
    staleTime: STALE_TIME.JOBS,
    placeholderData: (prev) => prev,
  });
}

/**
 * Hook to fetch a single job by ID
 * Automatically polls for updates when job is pending or running
 */
export function useJob(jobId: string) {
  return useQuery({
    queryKey: jobKeys.detail(jobId),
    queryFn: () => jobsApi.getJob(jobId),
    staleTime: STALE_TIME.JOB_DETAILS,
    enabled: !!jobId,
    // Poll every 3 seconds for live updates while job is active
    refetchInterval: (query) => {
      const job = query.state.data;
      if (job && (job.status === 'pending' || job.status === 'running')) {
        return 3000; // 3 seconds
      }
      return false; // Stop polling when job is completed/failed
    },
  });
}

/**
 * Hook to fetch recent jobs (for dashboard)
 */
export function useRecentJobs(limit = 5) {
  return useQuery({
    queryKey: [...jobKeys.lists(), 'recent', limit],
    queryFn: () => jobsApi.getRecentJobs(limit),
    staleTime: STALE_TIME.JOBS,
  });
}

/**
 * Hook to create a new job
 */
export function useCreateJob() {
  const queryClient = useQueryClient();
  const { success, error } = useNotification();

  return useMutation({
    mutationFn: (input: CreateJobInput) => jobsApi.createJob(input),
    onSuccess: (newJob) => {
      // Invalidate jobs list
      queryClient.invalidateQueries({ queryKey: jobKeys.lists() });

      // Add new job to cache
      queryClient.setQueryData(jobKeys.detail(newJob.id), newJob);

      success('Job created', `Scraping job for "${newJob.searchQuery}" started`);
    },
    onError: (err) => {
      error('Failed to create job', err instanceof Error ? err.message : 'Unknown error');
    },
  });
}

/**
 * Hook to update a job
 */
export function useUpdateJob(jobId: string) {
  const queryClient = useQueryClient();
  const { success, error } = useNotification();

  return useMutation({
    mutationFn: (input: UpdateJobInput) => jobsApi.updateJob(jobId, input),
    onSuccess: (updatedJob) => {
      // Update job in cache
      queryClient.setQueryData(jobKeys.detail(jobId), updatedJob);

      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: jobKeys.lists() });

      success('Job updated', `Job status changed to ${updatedJob.status}`);
    },
    onError: (err) => {
      error('Failed to update job', err instanceof Error ? err.message : 'Unknown error');
    },
  });
}

/**
 * Hook to delete a job
 */
export function useDeleteJob() {
  const queryClient = useQueryClient();
  const { success, error } = useNotification();

  return useMutation({
    mutationFn: (jobId: string) => jobsApi.deleteJob(jobId),
    onSuccess: (_, jobId) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: jobKeys.detail(jobId) });

      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: jobKeys.lists() });

      success('Job deleted', 'The job has been deleted');
    },
    onError: (err) => {
      error('Failed to delete job', err instanceof Error ? err.message : 'Unknown error');
    },
  });
}

/**
 * Hook to delete multiple jobs
 */
export function useDeleteJobs() {
  const queryClient = useQueryClient();
  const { success, error } = useNotification();

  return useMutation({
    mutationFn: (jobIds: string[]) => jobsApi.deleteJobs(jobIds),
    onSuccess: (_, jobIds) => {
      // Remove from cache
      jobIds.forEach((id) => {
        queryClient.removeQueries({ queryKey: jobKeys.detail(id) });
      });

      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: jobKeys.lists() });

      success('Jobs deleted', `${jobIds.length} jobs have been deleted`);
    },
    onError: (err) => {
      error('Failed to delete jobs', err instanceof Error ? err.message : 'Unknown error');
    },
  });
}

/**
 * Hook to pause a job
 */
export function usePauseJob() {
  const queryClient = useQueryClient();
  const { success, error } = useNotification();

  return useMutation({
    mutationFn: (jobId: string) => jobsApi.pauseJob(jobId),
    onSuccess: (updatedJob) => {
      queryClient.setQueryData(jobKeys.detail(updatedJob.id), updatedJob);
      queryClient.invalidateQueries({ queryKey: jobKeys.lists() });
      success('Job paused', 'The job has been paused');
    },
    onError: (err) => {
      error('Failed to pause job', err instanceof Error ? err.message : 'Unknown error');
    },
  });
}

/**
 * Hook to resume a job
 */
export function useResumeJob() {
  const queryClient = useQueryClient();
  const { success, error } = useNotification();

  return useMutation({
    mutationFn: (jobId: string) => jobsApi.resumeJob(jobId),
    onSuccess: (updatedJob) => {
      queryClient.setQueryData(jobKeys.detail(updatedJob.id), updatedJob);
      queryClient.invalidateQueries({ queryKey: jobKeys.lists() });
      success('Job resumed', 'The job has been resumed');
    },
    onError: (err) => {
      error('Failed to resume job', err instanceof Error ? err.message : 'Unknown error');
    },
  });
}

/**
 * Hook to cancel a job
 */
export function useCancelJob() {
  const queryClient = useQueryClient();
  const { success, error } = useNotification();

  return useMutation({
    mutationFn: (jobId: string) => jobsApi.cancelJob(jobId),
    onSuccess: (updatedJob) => {
      queryClient.setQueryData(jobKeys.detail(updatedJob.id), updatedJob);
      queryClient.invalidateQueries({ queryKey: jobKeys.lists() });
      success('Job cancelled', 'The job has been cancelled');
    },
    onError: (err) => {
      error('Failed to cancel job', err instanceof Error ? err.message : 'Unknown error');
    },
  });
}

/**
 * Hook to retry a failed job
 */
export function useRetryJob() {
  const queryClient = useQueryClient();
  const { success, error } = useNotification();

  return useMutation({
    mutationFn: (jobId: string) => jobsApi.retryJob(jobId),
    onSuccess: (updatedJob) => {
      queryClient.setQueryData(jobKeys.detail(updatedJob.id), updatedJob);
      queryClient.invalidateQueries({ queryKey: jobKeys.lists() });
      success('Job retried', 'The job is being retried');
    },
    onError: (err) => {
      error('Failed to retry job', err instanceof Error ? err.message : 'Unknown error');
    },
  });
}

/**
 * Hook to retry failed cells
 */
export function useRetryCells(jobId: string) {
  const queryClient = useQueryClient();
  const { success, error } = useNotification();

  return useMutation({
    mutationFn: (cellIds?: string[]) =>
      cellIds ? jobsApi.retryCells(jobId, cellIds) : jobsApi.retryAllFailedCells(jobId),
    onSuccess: (_, cellIds) => {
      queryClient.invalidateQueries({ queryKey: jobKeys.cells(jobId) });
      queryClient.invalidateQueries({ queryKey: jobKeys.progress(jobId) });
      success(
        'Cells retried',
        cellIds ? `${cellIds.length} cells are being retried` : 'All failed cells are being retried'
      );
    },
    onError: (err) => {
      error('Failed to retry cells', err instanceof Error ? err.message : 'Unknown error');
    },
  });
}

/**
 * Helper to prefetch a job
 */
export function usePrefetchJob() {
  const queryClient = useQueryClient();

  return (jobId: string) => {
    queryClient.prefetchQuery({
      queryKey: jobKeys.detail(jobId),
      queryFn: () => jobsApi.getJob(jobId),
      staleTime: STALE_TIME.JOB_DETAILS,
    });
  };
}
